import { Box, styled } from '@mui/material';
import { omit } from 'lodash';
import { FC } from 'react';
import {
  BooleanField,
  BooleanInput,
  NumberField,
  NumberInput,
  SelectField,
  SelectInput,
  TextField,
  TopToolbar,
  useGetIdentity,
} from 'react-admin';
import { JsonField } from 'react-admin-json-view';

import {
  BoExternalContractsQuery,
  GetBoExternalContractsOneResponse,
  getBoExternalContractsSortColumns,
} from '@Common/types/bo_external/contracts';

import {
  contractRelationshipTypeChoices,
  contractStatusChoices,
  contractTeamChoices,
  contractTypeChoices,
  paperTransactionStatementFrequencyChoices,
} from '@sh4res/bo-common/choices/contracts';
import { DatagridTyped, FilterButton, List } from '@sh4res/bo-common/components';
import {
  IdField,
  SelectArrayInputOrderedChoices,
  SelectChipField,
  TextArrayInput,
  UTCDateField,
} from '@sh4res/bo-common/fields';
import { useSelectedColumns } from '@sh4res/bo-common/hooks';
import { ColumnList, RaSort } from '@sh4res/bo-common/utils';
import { isB2bDistributor } from '@sh4res/bo-common/utils/b2b_auth_role';
import {
  CountryCodeChoices,
  useCountryCodeChoicesByName,
} from '@sh4res/bo-common/utils/countryChoices';
import { createFilters } from '@sh4res/bo-common/utils/create_filters';

const contractsFilters = ({ showCompanyId }: { showCompanyId: boolean }) => {
  const testId = 'BoExternalContractsList';
  const filterConfig = {
    id: <TextArrayInput asNumber source="" data-testid={`${testId}_IdFilter`} />,
    b2b_company_id: <TextArrayInput asNumber source="" data-testid={`${testId}_CompanyIdFilter`} />,
    contract_number: <NumberInput source="" data-testid={`${testId}_ContractNumberFilter`} />,
    relationship_type: (
      <SelectInput
        choices={contractRelationshipTypeChoices}
        data-testid={`${testId}_RelationshipTypeFilter`}
      />
    ),
    version: <NumberInput source="" data-testid={`${testId}_VersionFilter`} />,
    is_active: <BooleanInput source="" data-testid={`${testId}_IsActiveFilter`} />,
    is_liv: <BooleanInput source="" data-testid={`${testId}_IsLivFilter`} />,
    contract_type: (
      <SelectArrayInputOrderedChoices
        choices={contractTypeChoices}
        data-testid={`${testId}_ContractTypeFilter`}
      />
    ),
    not_status: (
      <SelectArrayInputOrderedChoices
        choices={contractStatusChoices}
        data-testid={`${testId}_NotStatusFilter`}
      />
    ),
  } as const;

  if (showCompanyId) {
    return createFilters<BoExternalContractsQuery>(filterConfig);
  } else {
    return createFilters<BoExternalContractsQuery>(
      omit(filterConfig, 'b2b_company_id') as typeof filterConfig,
    );
  }
};

const defaultSort: RaSort<BoExternalContractsQuery> = {
  field: 'created_at',
  order: 'DESC',
};

const createContractsListColumns = (
  countryCodeChoicesByName: CountryCodeChoices,
): ColumnList<GetBoExternalContractsOneResponse> => {
  const testId = 'BoExternalContractsList';
  return {
    id: <IdField reference="contracts" data-testid={`${testId}_IdColumn`} />,
    b2b_company_id: <IdField reference="companies" data-testid={`${testId}_CompanyIdColumn`} />,
    team: <SelectField choices={contractTeamChoices} data-testid={`${testId}_TeamColumn`} />,
    contract_number: <TextField data-testid={`${testId}_ContractNumberColumn`} />,
    contract_type: (
      <SelectChipField choices={contractTypeChoices} data-testid={`${testId}_ContractTypeColumn`} />
    ),
    status: (
      <SelectChipField choices={contractStatusChoices} data-testid={`${testId}_StatusColumn`} />
    ),
    effective_date: <UTCDateField data-testid={`${testId}_EffectiveDateColumn`} />,
    end_date: <UTCDateField data-testid={`${testId}_EndDateColumn`} />,
    is_active: <BooleanField data-testid={`${testId}_IsActiveColumn`} />,
    relationship_type: (
      <SelectChipField
        choices={contractRelationshipTypeChoices}
        data-testid={`${testId}_RelationshipTypeColumn`}
      />
    ),
    version: <NumberField data-testid={`${testId}_VersionColumn`} />,
    version_label: <TextField data-testid={`${testId}_VersionLabelColumn`} />,
    is_matching_contribution_displayed: (
      <BooleanField data-testid={`${testId}_IsMatchingContributionDisplayedColumn`} />
    ),
    paper_transaction_statement_frequency: (
      <SelectField
        choices={paperTransactionStatementFrequencyChoices}
        data-testid={`${testId}_PaperTransactionStatementFrequencyColumn`}
      />
    ),
    is_liv: <BooleanField data-testid={`${testId}_IsLivColumn`} />,
    is_group_contract: <BooleanField data-testid={`${testId}_IsGroupContractColumn`} />,
    participation: (
      <JsonField source="participation" data-testid={`${testId}_ParticipationColumn`} />
    ),
    profit_sharing: (
      <JsonField source="profit_sharing" data-testid={`${testId}_ProfitSharingColumn`} />
    ),
    value_sharing: (
      <JsonField source="value_sharing" data-testid={`${testId}_ValueSharingColumn`} />
    ),
    pee_agreement: (
      <JsonField source="pee_agreement" data-testid={`${testId}_PeeAgreementColumn`} />
    ),
    pereco_agreement: (
      <JsonField source="pereco_agreement" data-testid={`${testId}_PerecoAgreementColumn`} />
    ),
    pero_agreement: (
      <JsonField source="pero_agreement" data-testid={`${testId}_PeroAgreementColumn`} />
    ),
    transfer: <JsonField source="transfer" data-testid={`${testId}_TransferColumn`} />,
    campaigns: <JsonField source="campaigns" data-testid={`${testId}_CampaignsColumn`} />,
    created_at: <UTCDateField data-testid={`${testId}_CreatedAtColumn`} />,
    updated_at: <UTCDateField data-testid={`${testId}_UpdatedAtColumn`} />,
    dist_case_number: <TextField data-testid={`${testId}_DistCaseNumberColumn`} />,
    dist_case_reception_date: (
      <UTCDateField showDate data-testid={`${testId}_DistCaseReceptionDateColumn`} />
    ),
    dist_correspondent_lastname: (
      <TextField data-testid={`${testId}_DistCorrespondentLastnameColumn`} />
    ),
    dist_correspondent_firstname: (
      <TextField data-testid={`${testId}_DistCorrespondentFirstnameColumn`} />
    ),
    dist_correspondent_portfolio_code: (
      <TextField data-testid={`${testId}_DistCorrespondentPortfolioCodeColumn`} />
    ),
    dist_correspondent_orias_code: (
      <TextField data-testid={`${testId}_DistCorrespondentOriasCodeColumn`} />
    ),
    dist_correspondent_fia_code: (
      <TextField data-testid={`${testId}_DistCorrespondentFiaCodeColumn`} />
    ),
    dist_correspondent_email: <TextField data-testid={`${testId}_DistCorrespondentEmailColumn`} />,
    dist_correspondent_landline: (
      <TextField data-testid={`${testId}_DistCorrespondentLandlineColumn`} />
    ),
    dist_correspondent_phone_number: (
      <TextField data-testid={`${testId}_DistCorrespondentPhoneNumberColumn`} />
    ),
    dist_correspondent_collective_inspector_email: (
      <TextField data-testid={`${testId}_DistCorrespondentCollectiveInspectorEmailColumn`} />
    ),
    dist_correspondent_street: (
      <TextField data-testid={`${testId}_DistCorrespondentStreetColumn`} />
    ),
    filib_url: <TextField data-testid={`${testId}_FilibUrlColumn`} />,
    offer_code: <TextField data-testid={`${testId}_OfferCodeColumn`} />,
    dist_correspondent_postal_code: (
      <TextField data-testid={`${testId}_DistCorrespondentPostalCodeColumn`} />
    ),
    dist_correspondent_city: <TextField data-testid={`${testId}_DistCorrespondentCityColumn`} />,
    dist_correspondent_country: (
      <SelectField
        choices={countryCodeChoicesByName}
        data-testid={`${testId}_DistCorrespondentCountryColumn`}
      />
    ),
    matching_contribution_cut_off_date: (
      <TextField data-testid={`${testId}_MatchingContributionCutOffDateColumn`} />
    ),
    matching_contributions_enabled: (
      <BooleanField data-testid={`${testId}_MatchingContributionsEnabledColumn`} />
    ),
    matching_contribution_payment_frequency: (
      <TextField data-testid={`${testId}_MatchingContributionPaymentFrequencyColumn`} />
    ),
  } as ColumnList<GetBoExternalContractsOneResponse>;
};

const defaultColumns = ({
  showCompanyId,
}: {
  showCompanyId: boolean;
}): (keyof GetBoExternalContractsOneResponse)[] => [
  'id',
  ...(showCompanyId ? ['b2b_company_id' as const] : []),
  'contract_number',
  'is_active',
  'effective_date',
  'relationship_type',
  'version',
  'created_at',
];

const ContractsActionsItem = styled(Box)`
  margin: ${({ theme }) => theme.spacing(0, 0, 0, 0.5)};
`;

const ContractsActions = () => (
  <TopToolbar>
    <ContractsActionsItem>
      <FilterButton data-testid={'BoExternalContractsActions_FilterButton'} />
    </ContractsActionsItem>
  </TopToolbar>
);

export const ContractsList: FC = () => {
  const { data: identity } = useGetIdentity();

  const countryCodeChoicesByName = useCountryCodeChoicesByName();
  const contractsListColumns = createContractsListColumns(countryCodeChoicesByName);
  const displayedColumns = useSelectedColumns<GetBoExternalContractsOneResponse>({
    preferences: 'contracts.list.columns',
    defaultColumns: defaultColumns({ showCompanyId: isB2bDistributor(identity) }),
  });

  return (
    <List
      actions={<ContractsActions />}
      exporter={false}
      sort={defaultSort}
      filters={contractsFilters({ showCompanyId: isB2bDistributor(identity) })}
      empty={false}>
      <DatagridTyped<GetBoExternalContractsOneResponse>
        rowClick="show"
        bulkActionButtons={false}
        columnRegistry={contractsListColumns}
        displayedColumns={displayedColumns}
        sortableColumns={getBoExternalContractsSortColumns}
      />
    </List>
  );
};
